import {
  GamoraFoundationGuard,
  GamoraFoundationGuardConstructorType,
} from '../foundation/guard/guard.js';
import { assert } from '../utils/assert.js';
import { GamoraBase64Guard } from './base64.js';
import { GamoraBase64URLGuard } from './base64url.js';
import { GamoraErrorUnhandledException } from '../errors/unhandled-exception.error.js';
import { GamoraErrorValidation } from '../errors/validation.error.js';

/**
 * Input type
 */
export type GamoraUUIDGuardInput = string;

/**
 * GamoraUUIDGuard
 * A typesafe and guarded Universally Unique IDentifier (GamoraUUIDGuard) v4
 * @public
 */
export class GamoraUUIDGuard extends GamoraFoundationGuard<GamoraUUIDGuardInput> {
  protected _uuid = '';

  /**
   * Valid UUID Regex
   * @type {RegExp}
   */
  static RegExp =
    /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[1-5]{1}[0-9A-Fa-f]{3}-[ABab89]{1}[0-9A-Fa-f]{3}-[0-9A-Fa-f]{12}$/;

  /**
   * Constructor
   * @param {string} input the input
   */
  constructor(input: string) {
    super();
    this.uuid = input;
  }

  /**
   * Set uuid
   */
  set uuid(uuid: string) {
    assert(
      GamoraUUIDGuard.Test(uuid),
      'Invalid UUID, must be RFC 4122 (level 4) compliant.',
      GamoraErrorValidation,
    );
    this._uuid = uuid;
  }

  /**
   * Get uuid
   */
  get uuid() {
    return this._uuid;
  }

  /**
   * Gets from utf8
   * @param input Input string
   */
  static override fromUTF8<TClass extends GamoraUUIDGuard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraUUIDGuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    return new this(input);
  }

  /**
   * Gets from JSON
   * @param input Input string
   */
  static override fromJSON<TClass extends GamoraUUIDGuard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraUUIDGuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string',
      'Invalid input argument: not a string.',
      GamoraErrorValidation,
    );
    return new this(input);
  }

  /**
   * Gets from empty
   */
  static override Empty<TClass extends GamoraUUIDGuard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraUUIDGuardInput>,
  ) {
    return new this('00000000-0000-4000-8000-000000000000');
  }

  /**
   * Is empty
   */
  isEmpty() {
    return this.uuid === '00000000-0000-4000-8000-000000000000';
  }

  /**
   * Tests if input string is valid UUID
   * @param input The input string
   */
  static Test(input: string): boolean {
    return GamoraUUIDGuard.RegExp.test(input);
  }

  /**
   * Gets from Base64
   */
  static override fromBase64<TClass extends GamoraUUIDGuard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraUUIDGuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64Guard.fromBase64(input).toUTF8());
    } catch (e) {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64 input.',
      );
    }

    // Create new guard
    return new this(json as GamoraUUIDGuardInput);
  }

  /**
   * Gets from Base64URL
   */
  static override fromBase64URL<TClass extends GamoraUUIDGuard>(
    this: GamoraFoundationGuardConstructorType<TClass, GamoraUUIDGuardInput>,
    input: unknown,
  ) {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch (e) {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new this(json as GamoraUUIDGuardInput);
  }

  /**
   * Exports
   */
  override toBase64URL(): string {
    const json = this.toJSON();
    return GamoraBase64URLGuard.fromUTF8(JSON.stringify(json)).toBase64URL();
  }

  /**
   * Exports
   */
  override toBase64(): string {
    const json = this.toJSON();
    return GamoraBase64Guard.fromUTF8(JSON.stringify(json)).toBase64();
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return this.uuid;
  }

  /**
   * Gets uuid
   * @returns string
   */
  override toString() {
    return this.uuid;
  }

  /**
   * Clone
   */
  override clone() {
    return new (this.constructor as GamoraFoundationGuardConstructorType<
      this,
      GamoraUUIDGuardInput
    >)(this.toJSON());
  }
}
